import Icon from "@/components/icons";
import { Button, ButtonVariantsType } from "@/components/ui/button";
import { ElementType } from "@/constants/tagular/main";
import React, { Ref } from "react";
import { TagularEventingRequiredProps } from "@/types/Tagular";
import { BeamableComponentAttributes } from "@/components/CohesionReady/eventing/types";
import { fetchBeamableProperties } from "@/components/CohesionReady/eventing/helpers";

export type CTALinkProps = {
  enable_tracking?: boolean;
  label: string;
  url: string;
  type?: ButtonVariantsType;
  icon?: "launch" | "arrow-right" | "search";
  icon_position?: "left" | "right";
  size?: "default" | "sm" | "lg" | "xl" | "icon";
  radius?: "none" | "default" | "sm" | "md";
  is_external?: boolean;
  target?: string;
  name?: string;
  elementType?: ElementType;
  element_type?: ElementType;
  tagular_variant?: string;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  className?: string;
  styles?: string;
  //  ↓↓↓  WARN: this might be being missued and mistaken for tagular_variant or sometimes
  //  ↓↓↓  type (ButtonVariantsType) this seems especially true in the ProductCardListWrappers
  variant?: "link" | ButtonVariantsType | string;
  additionalClickEvent?: () => void;
  whitespace?: "whitespace-normal" | "whitespace-nowrap";
} & TagularEventingRequiredProps &
  BeamableComponentAttributes; // can handle both old and new tagular. (respectively)

export function renderCTAMarkup({
  label,
  url,
  type = "default",
  icon,
  icon_position = "right",
  size = "default",
  radius = "default",
  is_external = false,
  target = "_self",
  onClick,
  ref,
  variant,
  className,
  whitespace = "whitespace-normal",
  ...props
}: CTALinkProps) {
  const beamable = fetchBeamableProperties(props);

  let linkEventProps = {};
  let buttonEventProps = {};

  if (beamable && Object.keys(beamable).length) {
    linkEventProps = beamable;
  } else {
    buttonEventProps = { onClick: onClick, ref: ref };
  }

  const content = (
    <span
      className={`inline-flex gap-2 items-center justify-center break-words ${whitespace}`}
    >
      {icon && icon_position === "left" && <Icon type={icon} />}
      {label}
      {icon && icon_position === "right" && <Icon type={icon} />}
    </span>
  );

  const backgroundColor =
    props.styles && props.styles.includes("--cta-bg-color")
      ? props.styles.match(/--cta-bg-color:\s*([^;]+)/)?.[1]?.trim()
      : undefined;

  const link = () => (
    <a
      aria-label={label}
      href={url}
      {...linkEventProps}
      target={target}
      data-cta-custom-bg={backgroundColor || undefined}
      style={
        backgroundColor
          ? {
              backgroundColor,
              borderColor: backgroundColor,
            }
          : undefined
      }
    >
      {content}
    </a>
  );

  if (variant === "link") {
    linkEventProps = {
      onClick,
      ref: ref as Ref<HTMLAnchorElement>,
    };
    return link();
  }

  const customHoverStyle = backgroundColor
    ? `
      a[data-cta-custom-bg="${backgroundColor}"]:hover {
        background-color: #ffffff !important;
        color: ${backgroundColor} !important;
        border-color: ${backgroundColor} !important;
      }
    `
    : "";

  return (
    <>
      {customHoverStyle && (
        <style
          dangerouslySetInnerHTML={{
            __html: customHoverStyle,
          }}
        />
      )}
      <Button
        variant={type}
        radius={radius}
        size={size}
        asChild
        className={`not-prose ${className}`}
        {...buttonEventProps}
      >
        {link()}
      </Button>
    </>
  );
}
